import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * AuthCallback — handles Emergent Google OAuth return.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 *
 * Google auth returns to `{origin}/dashboard#session_id=...`. This component is
 * rendered synchronously by AppRoutes whenever `location.hash` contains
 * `session_id=`. It exchanges the session_id for a server-side session (cookie
 * set by backend), then navigates to /dashboard.
 */
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const match = (location.hash || "").match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/login", { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    (async () => {
      try {
        await api.post("/auth/google/session", { session_id: sessionId });
        // Clear the hash so refresh doesn't re-run the exchange
        window.history.replaceState(null, "", window.location.pathname);
        await refreshUser();
        toast.success("Signed in with Google.");
        navigate("/dashboard", { replace: true });
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Google sign-in failed. Please try again.");
        navigate("/login", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center">
      <div className="text-center">
        <p className="label-eyebrow mb-3">One moment</p>
        <p className="font-serif text-3xl text-forest animate-pulse">Signing you in…</p>
      </div>
    </div>
  );
}
